import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { resetTour } from "@/components/OnboardingTour";
import type { Organization, OrganizationRole, User, OrganizationMembership } from "@shared/schema";
import { Building2, Users, Trash2, Mail, Shield, UserPlus, Tags, Plus, X, User as UserIcon, RotateCcw } from "lucide-react";

export default function Settings() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<OrganizationRole>("editor");
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [customAttributes, setCustomAttributes] = useState<Record<string, string>>({});
  const [newAttrKey, setNewAttrKey] = useState("");
  const [newAttrValue, setNewAttrValue] = useState("");

  // Fetch user's organizations
  const { data: organizations } = useQuery<Array<Organization & { role: OrganizationRole }>>({
    queryKey: ["/api/organizations"],
  });

  const currentOrg = organizations?.[0];
  const isOwner = currentOrg?.role === "owner";
  const isAdmin = currentOrg?.role === "admin" || isOwner;

  // Initialize custom attributes from organization settings
  useEffect(() => {
    if (currentOrg?.settings && typeof currentOrg.settings === 'object') {
      const attrs = (currentOrg.settings as any).customAttributes || {};
      setCustomAttributes(attrs);
    }
  }, [currentOrg?.settings]);

  // Fetch organization members
  const { data: members, isLoading: membersLoading } = useQuery<
    Array<OrganizationMembership & { user: User }>
  >({
    queryKey: ["/api/organizations", currentOrg?.id, "members"],
    enabled: !!currentOrg?.id,
  });

  // Update organization mutation
  const updateOrgMutation = useMutation({
    mutationFn: async (data: { name: string; slug: string }) => {
      const res = await apiRequest("PUT", `/api/organizations/${currentOrg?.id}`, data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations"] });
      toast({
        title: "Success",
        description: "Organization settings updated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update organization",
        variant: "destructive",
      });
    },
  });

  // Update custom attributes mutation
  const updateAttributesMutation = useMutation({
    mutationFn: async (attributes: Record<string, string>) => {
      const res = await apiRequest("PUT", `/api/organizations/${currentOrg?.id}`, {
        settings: {
          customAttributes: attributes,
        },
      });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations"] });
      toast({
        title: "Success",
        description: "Custom attributes updated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update custom attributes",
        variant: "destructive",
      });
    },
  });

  // Invite member mutation
  const inviteMutation = useMutation({
    mutationFn: async (data: { email: string; role: OrganizationRole }) => {
      const res = await apiRequest("POST", `/api/organizations/${currentOrg?.id}/members/invite`, data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations", currentOrg?.id, "members"] });
      setIsInviteDialogOpen(false);
      setInviteEmail("");
      toast({
        title: "Success",
        description: "Team member invited successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to invite member",
        variant: "destructive",
      });
    },
  });

  // Update member role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ membershipId, role }: { membershipId: string; role: OrganizationRole }) => {
      const res = await apiRequest("PATCH", `/api/memberships/${membershipId}`, { role });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations", currentOrg?.id, "members"] });
      toast({
        title: "Success",
        description: "Member role updated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update role",
        variant: "destructive",
      });
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: async (membershipId: string) => {
      const res = await apiRequest("DELETE", `/api/memberships/${membershipId}`, undefined);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations", currentOrg?.id, "members"] });
      toast({
        title: "Success",
        description: "Member removed from organization",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove member",
        variant: "destructive",
      });
    },
  });

  const handleInviteMember = () => {
    if (!inviteEmail) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }
    inviteMutation.mutate({ email: inviteEmail, role: inviteRole });
  };

  const handleUpdateRole = (membershipId: string, newRole: OrganizationRole) => {
    updateRoleMutation.mutate({ membershipId, role: newRole });
  };

  const handleRemoveMember = (membershipId: string) => {
    if (confirm("Are you sure you want to remove this member from the organization?")) {
      removeMemberMutation.mutate(membershipId);
    }
  };

  const getRoleBadgeColor = (role: OrganizationRole) => {
    switch (role) {
      case "owner":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      case "admin":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "editor":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "viewer":
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
      default:
        return "";
    }
  };

  const handleAddAttribute = () => {
    if (!newAttrKey || !newAttrValue) {
      toast({
        title: "Error",
        description: "Please enter both a key and value",
        variant: "destructive",
      });
      return;
    }

    // Ensure key is in {key} format for template usage
    const formattedKey = newAttrKey.startsWith("{") ? newAttrKey : `{${newAttrKey}}`;
    
    const updatedAttrs = {
      ...customAttributes,
      [formattedKey]: newAttrValue,
    };
    
    setCustomAttributes(updatedAttrs);
    updateAttributesMutation.mutate(updatedAttrs);
    setNewAttrKey("");
    setNewAttrValue("");
  };

  const handleRemoveAttribute = (key: string) => {
    const { [key]: _, ...rest } = customAttributes;
    setCustomAttributes(rest);
    updateAttributesMutation.mutate(rest);
  };

  const handleRestartTour = () => {
    resetTour();
    toast({
      title: "Tour restarted",
      description: "The page will reload to start the onboarding tour.",
    });
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="organization" className="w-full">
        <TabsList>
          <TabsTrigger value="organization">
            <Building2 className="w-4 h-4 mr-2" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="team">
            <Users className="w-4 h-4 mr-2" />
            Team Members
          </TabsTrigger>
          <TabsTrigger value="attributes">
            <Tags className="w-4 h-4 mr-2" />
            Custom Attributes
          </TabsTrigger>
          <TabsTrigger value="preferences">
            <UserIcon className="w-4 h-4 mr-2" />
            Preferences
          </TabsTrigger>
        </TabsList>

        {/* Organization Settings Tab */}
        <TabsContent value="organization">
          <Card>
            <CardHeader>
              <CardTitle>Organization Settings</CardTitle>
              <CardDescription>
                Manage your organization's name and settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organization Name</Label>
                <Input
                  id="org-name"
                  defaultValue={currentOrg?.name}
                  placeholder="Your Church Name"
                  disabled={!isAdmin}
                  data-testid="input-settings-org-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="org-slug">Organization URL</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">streammanager.com/</span>
                  <Input
                    id="org-slug"
                    defaultValue={currentOrg?.slug}
                    placeholder="your-church"
                    disabled={!isOwner}
                    data-testid="input-settings-org-slug"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Only owners can change the organization URL
                </p>
              </div>

              {isAdmin && (
                <Button 
                  disabled={updateOrgMutation.isPending}
                  data-testid="button-save-org-settings"
                >
                  {updateOrgMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              )}

              {!isAdmin && (
                <p className="text-sm text-muted-foreground">
                  You need admin permissions to edit organization settings
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Members Tab */}
        <TabsContent value="team">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Team Members</CardTitle>
                  <CardDescription>
                    Manage who has access to your organization
                  </CardDescription>
                </div>
                {isAdmin && (
                  <Button 
                    onClick={() => setIsInviteDialogOpen(true)}
                    data-testid="button-invite-member"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite Member
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <p className="text-center text-muted-foreground py-8">Loading team members...</p>
              ) : !members || members.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No team members found</p>
              ) : (
                <div className="space-y-4">
                  {members.map((member) => {
                    const isCurrentUser = member.user.id === user?.id;
                    const canModify = isAdmin && !isCurrentUser && member.role !== "owner";

                    return (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                        data-testid={`member-${member.user.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center font-semibold">
                            {member.user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || member.user.email[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">
                              {member.user.name || member.user.email}
                              {isCurrentUser && (
                                <Badge variant="outline" className="ml-2">You</Badge>
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground">{member.user.email}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          {canModify ? (
                            <Select
                              value={member.role}
                              onValueChange={(value) => handleUpdateRole(member.id, value as OrganizationRole)}
                              disabled={updateRoleMutation.isPending}
                            >
                              <SelectTrigger className="w-32" data-testid={`select-role-${member.user.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="editor">Editor</SelectItem>
                                <SelectItem value="viewer">Viewer</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={getRoleBadgeColor(member.role)}>
                              {member.role}
                            </Badge>
                          )}

                          {canModify && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveMember(member.id)}
                              disabled={removeMemberMutation.isPending}
                              data-testid={`button-remove-${member.user.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <Separator className="my-6" />

              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Role Permissions
                </h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 mb-1">
                      Owner
                    </Badge>
                    <p className="text-muted-foreground ml-1">
                      Full control, can delete organization, manage all members
                    </p>
                  </div>
                  <div>
                    <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mb-1">
                      Admin
                    </Badge>
                    <p className="text-muted-foreground ml-1">
                      Manage streams, speakers, templates, invite editors and viewers
                    </p>
                  </div>
                  <div>
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 mb-1">
                      Editor
                    </Badge>
                    <p className="text-muted-foreground ml-1">
                      Create and edit streams, cannot manage team members
                    </p>
                  </div>
                  <div>
                    <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 mb-1">
                      Viewer
                    </Badge>
                    <p className="text-muted-foreground ml-1">
                      Read-only access to view streams and analytics
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Attributes Tab */}
        <TabsContent value="attributes">
          <Card>
            <CardHeader>
              <CardTitle>Custom Attributes</CardTitle>
              <CardDescription>
                Define custom attributes to use in your stream templates (e.g., {"{website}"}, {"{facebook}"})
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Existing Attributes */}
              {Object.keys(customAttributes).length > 0 ? (
                <div className="space-y-2">
                  <Label>Current Attributes</Label>
                  <div className="space-y-2">
                    {Object.entries(customAttributes).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between p-3 border rounded-lg bg-muted/30"
                        data-testid={`attr-${key}`}
                      >
                        <div className="flex-1 grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Placeholder</p>
                            <code className="text-sm font-mono bg-background px-2 py-1 rounded">
                              {key}
                            </code>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Value</p>
                            <p className="text-sm">{value}</p>
                          </div>
                        </div>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveAttribute(key)}
                            disabled={updateAttributesMutation.isPending}
                            data-testid={`button-remove-attr-${key}`}
                          >
                            <X className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No custom attributes defined yet. Add your first attribute below.
                </p>
              )}

              <Separator />

              {/* Add New Attribute */}
              {isAdmin ? (
                <div className="space-y-4">
                  <Label>Add New Attribute</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="attr-key" className="text-sm">Placeholder Name</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-muted-foreground">{"{"}</span>
                        <Input
                          id="attr-key"
                          value={newAttrKey}
                          onChange={(e) => setNewAttrKey(e.target.value.replace(/[{}]/g, ""))}
                          placeholder="website"
                          data-testid="input-attr-key"
                        />
                        <span className="text-sm text-muted-foreground">{"}"}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Enter name without braces (e.g., "website" becomes {"{website}"})
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="attr-value" className="text-sm">Value</Label>
                      <Input
                        id="attr-value"
                        value={newAttrValue}
                        onChange={(e) => setNewAttrValue(e.target.value)}
                        placeholder="https://yourchurch.com"
                        className="mt-1"
                        data-testid="input-attr-value"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        This value will replace the placeholder in templates
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleAddAttribute}
                    disabled={updateAttributesMutation.isPending || !newAttrKey || !newAttrValue}
                    data-testid="button-add-attribute"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Attribute
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  You need admin permissions to manage custom attributes
                </p>
              )}

              <Separator />

              {/* Help Section */}
              <div className="space-y-2 text-sm">
                <h4 className="font-medium">How to use custom attributes</h4>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground ml-2">
                  <li>Add a custom attribute with a name and value (e.g., "website" → "https://yourchurch.com")</li>
                  <li>Use the placeholder {"{attribute_name}"} in your stream templates</li>
                  <li>The placeholder will be automatically replaced with your defined value</li>
                  <li>Common examples: {"{website}"}, {"{facebook}"}, {"{instagram}"}, {"{email}"}</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences">
          <Card>
            <CardHeader>
              <CardTitle>User Preferences</CardTitle>
              <CardDescription>
                Manage your personal settings and preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Onboarding Tour</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Restart the interactive tour that guides you through StreamPilot's features.
                    This is helpful if you want to review the basics or show the platform to a new team member.
                  </p>
                  <Button
                    onClick={handleRestartTour}
                    variant="outline"
                    data-testid="button-restart-tour"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restart Onboarding Tour
                  </Button>
                </div>

                <Separator />

                <div className="space-y-2">
                  <h4 className="font-medium">Account Information</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center py-2">
                      <span className="text-muted-foreground">Name</span>
                      <span className="font-medium">{user?.name || "Not set"}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-muted-foreground">Email</span>
                      <span className="font-medium">{user?.email}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-muted-foreground">Role</span>
                      <Badge className={getRoleBadgeColor(currentOrg?.role || "viewer")}>
                        {currentOrg?.role || "viewer"}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite Member Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your organization
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@church.org"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="input-invite-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as OrganizationRole)}>
                <SelectTrigger id="invite-role" data-testid="select-invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsInviteDialogOpen(false)}
              data-testid="button-cancel-invite"
            >
              Cancel
            </Button>
            <Button
              onClick={handleInviteMember}
              disabled={inviteMutation.isPending}
              data-testid="button-send-invite"
            >
              {inviteMutation.isPending ? "Sending..." : "Send Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
